<template>
  <div>
    <menu-group class="itemheader">
      <h1 class="itemheader__title self-center">
        {{ $t("actions.createFlow") }}
      </h1>

      <button class="button-icon" type="button">
        <lucide-icon name="x" @click="closeMenu" />
      </button>
    </menu-group>

    <menu-group>
      <input-dropdown
        :options="allElements"
        :value="builderCtx.baseElement"
        prop="title"
        group-key="type"
        :item-key="(i) => `${i.type}_${i.id}`"
        :label="$t('labels.outgoingElement')"
        @update:value="selectBase"
      >
        <template #grouplabel="{ value: itemType }">
          <div class="options-list__grouplabel">
            {{ $t(`objectNames.${itemType}`, 3) }}
          </div>
        </template>
      </input-dropdown>
    </menu-group>
    <menu-group v-if="builderCtx.baseElement">
      <label class="label">{{ $t("labels.description") }}</label>
      <formatted-text
        v-if="builderCtx.baseElement.description.length > 0"
        :value="builderCtx.baseElement.description"
      />
      <p v-else class="message mt-0">
        {{ $t("errors.noDescription") }}
      </p>
      <label class="label mt-4">{{ $t("objectNames.indicator", 3) }}</label>
      <indicator-list
        :target-id="builderCtx.baseElement.id"
        :creatable="false"
        :editable="false"
      />
    </menu-group>

    <template v-if="builderCtx.endElement != null">
      <item-header
        :type="builderCtx.endElement.type"
        class="flows-builder-menu__elementselect"
        :label="$t(`labels.incoming.${builderCtx.endElement.type}`)"
        :button-centered="true"
        @close="selectEnd(null)"
      >
        <template #content>
          <input-dropdown
            :options="otherElements"
            prop="title"
            :select="endElementIndex"
            @select="selectEnd($event.key)"
          />
        </template>
      </item-header>
      <menu-group>
        <label class="label">{{ $t("labels.description") }}</label>
        <formatted-text
          v-if="builderCtx.endElement.description.length > 0"
          :value="builderCtx.endElement.description"
        />
        <p v-else class="message mt-0">
          {{ $t("errors.noDescription") }}
        </p>

        <template v-if="builderCtx.endElement.type === 'target'">
          <label class="label mt-4">{{ $t("objectNames.indicator", 3) }}</label>
          <indicator-list
            :target-id="builderCtx.endElement.id"
            :creatable="false"
            :editable="false"
          />
        </template>
      </menu-group>
    </template>

    <menu-group v-if="builderCtx.endElement == null">
      <label class="label">{{ $t("labels.elementOverview") }}</label>
      <p v-if="otherElements.length == 0">
        {{ $t("errors.noElementsToConnect") }}
      </p>
      <div v-else class="list">
        <div
          v-for="item in otherElements"
          :key="item.id"
          class="flows-builder-menu__endelement"
        >
          <button-item
            @click="selectItem(item)"
            @pointerenter="highlightItem(item)"
            @pointerout="unHighlightItem(item)"
          >
            {{ item.title }}
          </button-item>
          <div class="flows-builder-menu__endelement__status">
            <span v-if="outgoingRelations[`${item.type}.${item.id}`] != null">
              <lucide-icon
                v-if="
                  outgoingRelations[`${item.type}.${item.id}`].updateType ==
                  'none'
                "
                name="x"
              />
              <template v-else>
                <lucide-icon
                  v-if="outgoingRelations[`${item.type}.${item.id}`].inverse"
                  name="inverse"
                />
                <lucide-icon
                  v-if="!outgoingRelations[`${item.type}.${item.id}`].inverse"
                  name="not-inverse"
                />
              </template>
            </span>
          </div>
        </div>
      </div>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { mapKeys } from "lodash-es";

import MenuGroup from "@core/ui/MenuGroup.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputDropdown from "@core/input/InputDropdown.vue";

import IndicatorList from "@editor/items/indicator/IndicatorList.vue";

import { useFlowBuilder } from "./flowBuilder.js";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "../resource/resources.store";

import { useFeatures } from "@editor/store/features.store";
import { useIndexStore } from "../index/indices.store";
import { useModelViewStore } from "@editor/views/model/modelView.store";

import type { ResourceItem } from "../resource/ResourceItem";

const features = useFeatures();
const indexStore = useIndexStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const modelViewStore = useModelViewStore();

const builderCtx = useFlowBuilder();

const allElements = computed(() => {
  const enabledElements = [];
  if (features.target) {
    enabledElements.push(...targetStore.orderedItems);
  }
  if (features.resource) {
    enabledElements.push(...resourceStore.orderedItems);
  }
  if (features.index) {
    enabledElements.push(...indexStore.orderedItems);
  }
  return enabledElements;
});

const otherElements = computed(() => {
  let resources: ResourceItem[] = [];
  
  resources = resourceStore.orderedItems.filter(
    (res) =>
      res.id != builderCtx.baseElement?.id ||
      builderCtx.baseElement.type != "resource"
  );

  const targets = targetStore.orderedItems.filter(
    (el) =>
      el.id != builderCtx.baseElement?.id || builderCtx.baseElement.type != "target"
  );
  return [...targets, ...resources];
});

const endElementIndex = computed(() => {
  return otherElements.value.findIndex(
    (item) =>
      item.type === builderCtx.endElement?.type &&
      item.id === builderCtx.endElement.id
  );
});

const outgoingRelations = computed(() => {
  if (builderCtx.baseElement == null) return {};
  const baseElementUid = `${builderCtx.baseElement.type}_${builderCtx.baseElement.id}`;
  return mapKeys(
    relationStore.byStartUid[baseElementUid],
    (relation) => `${relation.endItemType}.${relation.endItemId}`
  );
});

function selectItem(item: { type: "target" | "resource"; id: number }) {
  selectionStore.clearHighlights();
  selectionStore.select(item);
}

function highlightItem(item: { type: "target" | "resource"; id: number }) {
  selectionStore.setHighlight(item);
}

function unHighlightItem(item: { type: "target" | "resource"; id: number }) {
  selectionStore.removeHighlight(item);
}

function selectBase(item: { type: "target" | "resource"; id: number }) {
  const { id, type } = item;
  selectionStore.clearAll();
  selectionStore.select({ id: id, type: type });
  modelViewStore.flowSourceElement = { uid: `${type}_${id}` };
}

function selectEnd(itemIndex: number | null) {
  if (itemIndex == null) {
    selectionStore.clear();
  } else {
    const item = otherElements.value[itemIndex];
    selectionStore.select(item);
  }
}

function closeMenu() {
  modelViewStore.flowSourceElement = null;
}
</script>

<style lang="scss">
.flows-builder-menu {
  &__elementselect {
    background-color: transparent;
  }
  &__endelement {
    display: flex;

    &__status {
      width: 4rem;
      flex-grow: false;
      text-align: right;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>
